import { com } from '@eidu/web'
import React from 'react'
import { iconSizes, Tooltip } from '@common/ui'
import { IconAlertTriangle } from '@common/ui/icons'
import EntityReferenceValue from '../data/EntityReferenceValue'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import FieldType = com.eidu.entity.field.FieldType
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityId = com.eidu.entity.EntityId
import entityIdFromStringOrNull = com.eidu.entity.entityIdFromStringOrNull

export type CellContent = {
  value?: string
  error?: string
}

export type RenderCellParams<V> = {
  value?: V
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

export const stringOrNumberComparator = (a?: string | number, b?: string | number): number => {
  const aEmpty = a == null || a === ''
  const bEmpty = b == null || b === ''
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return collator.compare(String(a), String(b))
}

export const cellContentComparator = (v1: CellContent, v2: CellContent): number =>
  stringOrNumberComparator(v1.value, v2.value)

type RenderCellFunction = (params: RenderCellParams<CellContent>) => React.JSX.Element | null

const getRenderFieldCell = (
  fieldType: FieldType,
  entities: ReadonlyMap<EntityId, EntityWithLabel | null>,
  types: ReadonlyMap<EntityTypeId, EntityType>
): RenderCellFunction => {
  const renderValidationError = (error?: string) =>
    error && (
      <Tooltip label={error}>
        <IconAlertTriangle
          size={iconSizes.sm}
          stroke={1.5}
          color="var(--trellis-color-caution)"
          style={{ marginInline: 8, flexShrink: 0 }}
        />
      </Tooltip>
    )

  switch (fieldType) {
    case FieldType.Reference:
      return ({ value }) => {
        const fieldValue = value?.value
        const referencedId = fieldValue?.let((it) => entityIdFromStringOrNull(it) ?? undefined)
        const referencedEntity = referencedId?.let((it) => entities.get(it)) ?? undefined
        const type = referencedEntity?.let((it) => types.get(it.entity.typeId))
        const typeName = type?.let((it) => it.name)

        const empty = value !== undefined && value.value === undefined

        return (
          <>
            {renderValidationError(value?.error)}
            {!empty && <EntityReferenceValue id={referencedId} entity={referencedEntity} typeName={typeName} />}
          </>
        )
      }

    case FieldType.ReferenceSet:
      return ({ value }) => {
        const count = value?.value ? value.value.split(', ').length : 0
        return (
          <>
            {renderValidationError(value?.error)}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{count}</span>
          </>
        )
      }

    case FieldType.Text:
    case FieldType.Number:
    case FieldType.Date:
    default:
      return ({ value }) => (
        <>
          {renderValidationError(value?.error)}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value?.value || ''}</span>
        </>
      )
  }
}

export default getRenderFieldCell
